import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { User } from '@core/generated/gql-types';
import { TemplateTerm } from '@core/template/useTemplateStore';
import { formatUserDisplayName } from '@/utils/utils';
import {
  getOnboardingPercentageFromWorker,
  getWorkerIndustryNames,
  getWorkerSkillNames,
  type ClassificationStoreParams,
} from './workerFilters.utils';

dayjs.extend(relativeTime);

export type WorkerColumnParams = {
  getTerm: (term: TemplateTerm, plural: boolean) => string;
  getStatusBadgeClass: (status?: string) => string;
  getOnboardingPercentageStyle: (percentage: string) => string;
  classificationStoreParams: ClassificationStoreParams;
  getClassificationById: (id: string) => { name?: string | null } | null;
  getClassificationLevelById: (id: string) => { name?: string | null } | null;
  getWorkerTransport: (worker: User) => string;
  getWorkerGender: (worker: User) => string;
  getWorkerJobInfo: (workerId: string) => {
    completedCount: number;
    lastJobEndDate?: string | null;
  };
  getTenantUserById: (id: string) => User | null;
};

export type WorkerColumn = {
  key: string;
  title: string;
  width: number;
  sortable: boolean;
  renderCell: (worker: User) => React.ReactNode;
  cellClassName: string;
};

export const createWorkerColumns = (
  params: WorkerColumnParams,
): WorkerColumn[] => {
  const {
    getTerm,
    getStatusBadgeClass,
    getOnboardingPercentageStyle,
    classificationStoreParams,
    getClassificationById,
    getClassificationLevelById,
    getWorkerTransport,
    getWorkerGender,
    getWorkerJobInfo,
    getTenantUserById,
  } = params;

  return [
    {
      key: 'candidate',
      title: getTerm(TemplateTerm.WORKER, false),
      width: 200,
      sortable: true,
      renderCell: (worker: User) => {
        const name =
          `${worker.firstName || ''} ${worker.lastName || ''}`.trim() ||
          'Unknown';
        return (
          <div className="flex items-center gap-2">
            {worker.profilePictureUrl && (
              <img
                src={worker.profilePictureUrl}
                alt={name}
                className="h-8 w-8 rounded-full object-cover"
              />
            )}
            <span className="text-sm text-gray-900">{name}</span>
          </div>
        );
      },
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden',
    },
    {
      key: 'status',
      title: 'Status',
      width: 140,
      sortable: true,
      renderCell: (worker: User) => (
        <span
          className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusBadgeClass(worker.status || undefined)}`}
        >
          {worker.status || 'Unknown'}
        </span>
      ),
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden whitespace-nowrap',
    },
    {
      key: 'onboardingPercentage',
      title: 'Onboarding %',
      width: 170,
      sortable: true,
      renderCell: (worker: User) => {
        const percentageStr = getOnboardingPercentageFromWorker(worker);
        const display = percentageStr === '-' ? '...' : `${percentageStr}%`;
        return (
          <div className="flex items-center gap-1">
            <span
              className={`rounded-full px-2 py-1 text-xs font-medium ${getOnboardingPercentageStyle(percentageStr === '-' ? '-' : percentageStr)}`}
            >
              {display}
            </span>
          </div>
        );
      },
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden',
    },
    {
      key: 'phoneNumber',
      title: 'Phone number',
      width: 150,
      sortable: false,
      renderCell: (worker: User) => (
        <div className="text-sm text-gray-900">
          {worker?.phoneNumber || '-'}
        </div>
      ),
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden',
    },
    {
      key: 'assignedAdmin',
      title: 'Assigned consultant',
      width: 170,
      sortable: true,
      renderCell: (worker: User) => {
        const assignedAdminId = worker.assignedAdminId;
        if (!assignedAdminId)
          return <div className="text-sm text-gray-900">-</div>;

        const assignedAdmin = getTenantUserById(assignedAdminId);
        if (!assignedAdmin)
          return <div className="text-sm text-gray-900">Unknown admin</div>;

        return (
          <div className="text-sm text-gray-900">
            {formatUserDisplayName(assignedAdmin)}
          </div>
        );
      },
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden whitespace-nowrap',
    },
    {
      key: 'industry',
      title: 'Industry',
      width: 200,
      sortable: true,
      renderCell: (worker: User) => {
        const industryNames = getWorkerIndustryNames(
          worker,
          classificationStoreParams,
        );
        if (industryNames.length === 0) {
          return <div className="text-sm text-gray-900">-</div>;
        }
        return (
          <div className="flex flex-wrap items-center gap-1">
            {industryNames.map((name) => (
              <span
                key={name}
                className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-800"
              >
                {name}
              </span>
            ))}
          </div>
        );
      },
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden',
    },
    {
      key: 'skillSet',
      title: 'Skill set',
      width: 180,
      sortable: false,
      renderCell: (worker: User) => {
        const skillNames = getWorkerSkillNames(worker, {
          getClassificationById,
          getClassificationLevelById,
        });
        const firstSkill = skillNames[0] || '';
        const additionalCount = skillNames.length - 1;
        return (
          <div className="flex items-center gap-1">
            <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-800">
              {firstSkill}
            </span>
            {additionalCount > 0 && (
              <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-800">
                +{additionalCount}
              </span>
            )}
          </div>
        );
      },
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden',
    },
    {
      key: 'state',
      title: 'State',
      width: 120,
      sortable: true,
      renderCell: (worker: User) => (
        <div className="text-sm text-gray-900">
          {worker.address?.state || '-'}
        </div>
      ),
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden',
    },
    {
      key: 'suburb',
      title: 'Suburb',
      width: 120,
      sortable: true,
      renderCell: (worker: User) => (
        <div className="text-sm text-gray-900">
          {worker.address?.suburb || '-'}
        </div>
      ),
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden',
    },
    {
      key: 'postcode',
      title: 'Postcode',
      width: 100,
      sortable: true,
      renderCell: (worker: User) => (
        <div className="text-sm text-gray-900">
          {worker.address?.postcode || '-'}
        </div>
      ),
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden whitespace-nowrap',
    },
    {
      key: 'transport',
      title: 'Transport',
      width: 140,
      sortable: true,
      renderCell: (worker: User) => (
        <div className="text-sm text-gray-900">
          {getWorkerTransport(worker)}
        </div>
      ),
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden',
    },
    {
      key: 'gender',
      title: 'Gender',
      width: 100,
      sortable: true,
      renderCell: (worker: User) => (
        <div className="text-sm text-gray-900">{getWorkerGender(worker)}</div>
      ),
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden whitespace-nowrap',
    },
    {
      key: 'jobsCompleted',
      title: 'Jobs completed',
      width: 140,
      sortable: true,
      renderCell: (worker: User) => {
        const { completedCount } = getWorkerJobInfo(worker.id);
        return <div className="text-sm text-gray-900">{completedCount}</div>;
      },
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden whitespace-nowrap',
    },
    {
      key: 'startDate',
      title: 'Sign up date',
      width: 140,
      sortable: true,
      renderCell: (worker: User) => {
        const startDate = worker.cognitoCreatedAt;
        return (
          <div className="text-sm text-gray-900">
            {startDate
              ? dayjs(parseInt(startDate, 10)).format('D MMM YYYY')
              : '-'}
          </div>
        );
      },
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden whitespace-nowrap',
    },
    {
      key: 'lastUpdatedAt',
      title: 'Last updated at',
      width: 160,
      sortable: true,
      renderCell: (worker: User) => {
        const updatedAt = worker.updatedAt;
        return (
          <div className="text-sm text-gray-900">
            {updatedAt
              ? dayjs(parseInt(updatedAt, 10)).format('D MMM YYYY')
              : '-'}
          </div>
        );
      },
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden whitespace-nowrap',
    },
    {
      key: 'lastJob',
      title: 'Last job',
      width: 120,
      sortable: true,
      renderCell: (worker: User) => {
        const { lastJobEndDate } = getWorkerJobInfo(worker.id);
        if (!lastJobEndDate)
          return <div className="text-sm text-gray-900">-</div>;

        const timeAgo = dayjs(parseInt(lastJobEndDate)).fromNow();
        return <div className="text-sm text-gray-900">{timeAgo}</div>;
      },
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden whitespace-nowrap',
    },
  ];
};
